import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, ThumbsUp, Sparkles } from 'lucide-react';
import { formatSourceDataValue } from '@/lib/formatting';

interface FactData {
  fact: string;
  source: string | { text: string; url: string };
  sourceType?: string;
  sourceQueueId?: string;
  sourceData?: any;
  metadata?: any;
}

interface FactDetailViewProps {
  fact: FactData;
  index: number;
  onBack: () => void;
  onSelect: (fact: FactData) => void;
}

export function FactDetailView({ fact, index, onBack, onSelect }: FactDetailViewProps) {
  const isProcessorFact = fact.sourceType === 'tapscan' || fact.sourceType === 'profile';
  
  // Parse source data for display
  const getSourceVariables = () => {
    if (!fact.sourceData) return null;
    
    try {
      const data = typeof fact.sourceData === 'string' 
        ? JSON.parse(fact.sourceData) 
        : fact.sourceData;
      
      // Handle NPR Profile data
      if (fact.sourceType === 'profile' && data) {
        const variables = [];
        if (data.item) variables.push({ label: 'Original Text', value: data.item });
        if (data.category) variables.push({ label: 'Category', value: data.category });
        if (data.subcategory) variables.push({ label: 'Subcategory', value: data.subcategory });
        if (data.us_percentage !== undefined) {
          variables.push({ label: 'US %', value: formatSourceDataValue('US %', `${data.us_percentage}%`) });
        }
        if (data.npr_percentage !== undefined) {
          variables.push({ label: 'NPR %', value: formatSourceDataValue('NPR %', `${data.npr_percentage}%`) });
        }
        if (data.metricType !== 'percentage' && data.index !== undefined) {
          variables.push({ label: 'Index', value: formatSourceDataValue('Index', data.index) });
        }
        return variables.length > 0 ? variables : null;
      }
      
      // Handle Tapscan data
      if (fact.sourceType === 'tapscan' && data) {
        const variables = [];
        if (data.label) variables.push({ label: 'Original Text', value: data.label });
        if (data.pct !== undefined) {
          // Convert decimal to percentage (0.024 -> 2.4%)
          const pctValue = parseFloat(data.pct);
          if (!isNaN(pctValue)) {
            const displayPct = pctValue <= 1 ? (pctValue * 100).toFixed(1) : pctValue.toFixed(1);
            variables.push({ label: 'Percentage', value: formatSourceDataValue('Percentage', `${displayPct}%`) });
          }
        } else if (data.index !== undefined) {
          variables.push({ label: 'Index', value: formatSourceDataValue('Index', data.index) });
        }
        if (data.category) variables.push({ label: 'Category', value: data.category });
        if (data.subcategory) variables.push({ label: 'Subcategory', value: data.subcategory });
        return variables.length > 0 ? variables : null;
      }
      
      // For other types
      if (data && typeof data === 'object') {
        const entries = Object.entries(data)
          .filter(([key, value]) => value !== null && value !== undefined && value !== '')
          .map(([key, value]) => ({
            label: key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, ' '),
            value: formatSourceDataValue(key, value)
          }));
        return entries.length > 0 ? entries : null;
      }
    } catch (error) {
      console.error('Error parsing source data:', error);
    }
    
    return null;
  };

  const sourceVariables = getSourceVariables();

  return (
    <div className="animate-in fade-in-50 slide-in-from-right-10 duration-300">
      <Card className="w-full max-w-4xl mx-auto">
        <CardHeader className="border-b">
          <div className="flex justify-between items-start">
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="sm"
                onClick={onBack}
                className="hover:bg-gray-100"
                data-testid="button-back-to-facts"
              >
                <ArrowLeft className="h-4 w-4 mr-1" />
                Back to Facts
              </Button>
              <Badge variant="secondary" className="text-sm">
                Fact {index + 1}
              </Badge>
            </div>
          </div>
        </CardHeader>
        
        <CardContent className="p-6 space-y-6">
          {/* Fact Text */}
          <div className="space-y-2">
            <h3 className="text-sm font-semibold text-gray-500">Fact</h3>
            <p className="text-base leading-relaxed">{fact.fact}</p>
          </div>

          {/* Source Citation */}
          <div className="space-y-2">
            <h3 className="text-sm font-semibold text-gray-500">Source</h3>
            <div className="text-sm">
              {typeof fact.source === 'string' ? (
                <span className="text-gray-700 italic">{fact.source}</span>
              ) : (
                <a 
                  href={fact.source.url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-800 underline"
                >
                  {fact.source.text}
                </a>
              )}
            </div>
          </div>

          {/* Source Data Variables */}
          {isProcessorFact && sourceVariables && sourceVariables.length > 0 && (
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-gray-500">Verify Source Data</h3>
              <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                {sourceVariables.map((variable, idx) => (
                  <div key={idx} className="flex flex-col sm:flex-row sm:items-start">
                    <span className="font-medium text-gray-600 min-w-[140px] mb-1 sm:mb-0">
                      {variable.label}:
                    </span>
                    <span className="text-gray-900 break-words flex-1">
                      {variable.value}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Action Button */}
          <div className="pt-4">
            {isProcessorFact ? (
              <Button
                className="w-full sm:w-auto bg-green-600 hover:bg-green-700 text-white"
                onClick={() => {
                  onSelect(fact);
                  onBack();
                }}
                data-testid={`button-verify-detail-${index}`}
              >
                <ThumbsUp className="h-4 w-4 mr-2" />
                Looks Good - Select This Fact
              </Button>
            ) : (
              <Button
                variant="default"
                className="w-full sm:w-auto"
                onClick={() => {
                  onSelect(fact);
                  onBack();
                }}
                data-testid={`button-select-detail-${index}`}
              >
                <Sparkles className="h-4 w-4 mr-2" />
                Select This Fact
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}